import clsx from 'clsx';
import { Form, Formik } from 'formik';
import React, { FC, memo, useCallback, useEffect, useState } from 'react';
import FnAntModal from '../../components/blocks/FnAntModal/index';
import { FnButtonVariants } from '../../components/elements/FnButton/constants';
import FnPerfectScrollbar from '../../components/elements/FnPerfectScrollbar/index';
import FormSection from './components/FormSection/index';
import ProductDescription from '../../components/elements/FnProductDescription/index';
import Subheader from './components/SubHeader/index';
import { ConfigurationChanges } from './constants';
import ApplyChanges from './components/FormComponents/ApplyChanges/index';
import { DivWrapper, DivColumn, DivFormWrapper, DivStep } from './styles';
import {
  getArtworkConfiguration,
  getBlankConfiguration,
  getColorgateConfigurationOptions,
  getInitialColorgateConfiguration
} from './utils';
import { TColorgateConfigurationResponse, TData } from './types';
import { FnPerfectScrollbarVariants } from '../../components/elements/FnPerfectScrollbar/constants';
import MimSettingsBlock from './components/Blocks/MimSettingsBlock/index';
import SpecialColorsBlock from './components/Blocks/SpecialColorsBlock/index';
import PrintSetupBlock from './components/Blocks/PrintSetupBlock/index';
import SizeRotationPositionBlock from './components/Blocks/SizeRotationPositionBlock/index';
import ColorBlock from './components/Blocks/ColorBlock/index';
import RipOutputOpacityBlock from './components/Blocks/RipOutputOpacity/index';

type TProps = {
  data: TData;
  onSave: (data: TColorgateConfigurationResponse) => void;
  onClose: () => void;
};

const ColorgateConfigurationModal: FC<TProps> = ({ data, onSave, onClose }) => {
  const [step, setStep] = useState(0);
  const [product, setProduct] = useState(null);
  const [options, setOptions] = useState(null);
  const [initialValues, setInitialValues] = useState(null);
  const [scrollbarRef, setScrollbarRef] = useState(null);

  useEffect(() => {
    if (data) {
      setProduct(data.formData);
      setOptions(getColorgateConfigurationOptions(data.formData));
      setInitialValues(
        getInitialColorgateConfiguration({
          artworkConfiguration: data.formData.object.artworkConfiguration,
          blankConfiguration: data.formData.object.blankConfiguration,
          color: data.description.color
        })
      );
    }
  }, [data]);

  const handleClose = () => onClose();

  const handleSubmitForm = (values) => {
    const blankConfiguration = getBlankConfiguration(values);
    const artworkConfiguration = getArtworkConfiguration(values);
    const newObject = { blankConfiguration, artworkConfiguration };
    const newData = { ...product, object: { ...newObject } };

    onSave(newData);
  };

  const handleChangeStep = useCallback(
    (index) => {
      setStep(index);
      if (scrollbarRef) {
        scrollbarRef.scrollTop = 0;
      }
    },
    [step, setStep, scrollbarRef]
  );

  return (
    initialValues !== null &&
    options !== null && (
      <Formik initialValues={initialValues} onSubmit={(fields) => handleSubmitForm(fields)}>
        {({ submitForm, values }) => (
          <Form>
            <FnAntModal
              titleText="DTG Ripping Software Configuration"
              isOpen={true}
              onClose={handleClose}
              okText={step === 0 ? 'Go to set implementation' : 'Save'}
              okVariant={step === 0 ? FnButtonVariants.Secondary : FnButtonVariants.Primary}
              onOk={step === 0 ? () => handleChangeStep(1) : () => submitForm()}
              cancelText={step === 0 ? '' : 'Cancel'}
              onCancel={onClose}
              footerShadow={true}
              footerCentered={true}
              noPadding={true}
              noTitleLine={true}
            >
              <DivWrapper>
                <Subheader onChangeStep={(index) => handleChangeStep(index)} activeStep={step} />
                <FnPerfectScrollbar
                  containerRef={(ref) => setScrollbarRef(ref)}
                  component="div"
                  styled
                  variant={FnPerfectScrollbarVariants.Secondary}
                >
                  <DivFormWrapper>
                    <ProductDescription
                      data={{
                        ...data.description,
                        remark: '* Changes will be implemented on all SKUs that are defined in the same size group'
                      }}
                      className="modal-body colorgate-configuration-modal"
                    />
                    <DivStep className={clsx({ ['active']: step === 0 })}>
                      <FormSection title="Blank / Garment">
                        <DivColumn>
                          <MimSettingsBlock options={options} />
                          <SpecialColorsBlock options={options} values={values} />
                        </DivColumn>
                        <DivColumn>
                          <PrintSetupBlock options={options} />
                        </DivColumn>
                      </FormSection>

                      <FormSection title="Decoration / Artwork">
                        <DivColumn>
                          <SizeRotationPositionBlock options={options} values={values} />
                        </DivColumn>
                        <DivColumn>
                          <ColorBlock options={options} />
                          <RipOutputOpacityBlock />
                        </DivColumn>
                      </FormSection>
                    </DivStep>
                    <DivStep className={clsx({ ['active']: step === 1 })}>
                      <FormSection title="Blank / Garment configuration changes">
                        <DivColumn className="full-width">
                          <ApplyChanges
                            type={ConfigurationChanges.Blank}
                            values={values}
                            initialValues={initialValues}
                            sku={data.description.sku}
                          />
                        </DivColumn>
                      </FormSection>
                      <FormSection title="Decoration / Artwork configuration changes">
                        <DivColumn className="full-width">
                          <ApplyChanges
                            type={ConfigurationChanges.Artwork}
                            values={values}
                            initialValues={initialValues}
                            sku={data.description.sku}
                          />
                        </DivColumn>
                      </FormSection>
                    </DivStep>
                  </DivFormWrapper>
                </FnPerfectScrollbar>
              </DivWrapper>
            </FnAntModal>
          </Form>
        )}
      </Formik>
    )
  );
};

export default memo(ColorgateConfigurationModal);
